export const EXPERIENCE_PHASES = Object.freeze(['banner', 'overlay', 'shrink', 'video-ready', 'playing']);

const LOTTERY_PRIZES = Object.freeze({
  308: Object.freeze({ key: 'opendots-one', rotation: 1404 }),
  309: Object.freeze({ key: 'sport-kit', rotation: 1332 }),
  310: Object.freeze({ key: 'travel-bag', rotation: 1188 }),
  311: Object.freeze({ key: 'better-luck', rotation: 1260 }),
});

const LOTTERY_ERROR_KEYS = Object.freeze({
  no_login: 'no-login',
  no_act: 'no-act',
  no_finish: 'no-finish',
  no_finish_ip: 'no-finish-ip',
});

const LOTTERY_ERROR_COPY_KEYS = Object.freeze({
  'no-login': 'errorNoLogin',
  'no-act': 'errorNoAct',
  'no-finish': 'errorNoFinish',
  'no-finish-ip': 'errorNoFinishIp',
  unknown: 'errorUnknown',
});

const OE_AUDIO_LIST_ID = 'oe_audio';
const OE_AUDIO_ITEM_LIST_NAME = `Navigation _ ${OE_AUDIO_LIST_ID}`;
const OE_AUDIO_CURRENCY = 'EUR';
const SUBSCRIPTION_FAILURE_DELAY_MS = 5000;

function cleanLabel(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function centsToCurrency(value) {
  const cents = Number(value);
  return Number.isFinite(cents) ? cents / 100 : 0;
}

function getProductValueMap() {
  if (typeof product_handle_valueMap !== 'undefined') return product_handle_valueMap;
  return {};
}

export function buildSelectContentParameters(contentType, contentName, buttonName) {
  const typeLabel = cleanLabel(contentType);
  const nameLabel = cleanLabel(contentName);
  const buttonLabel = cleanLabel(buttonName);
  if (!typeLabel || !buttonLabel) return null;

  return {
    content_type: typeLabel,
    content_name: nameLabel,
    button_name: buttonLabel,
  };
}

export function buildSelectItemParameters(productData, productValueMap = {}) {
  if (!productData) return null;

  const sku = cleanLabel(productData.sku);
  const fallbackTitle = cleanLabel(productData.title);
  const buttonName = cleanLabel(productData.buttonName);
  if (!sku || !fallbackTitle || !buttonName) return null;

  const mapped = (sku && productValueMap?.[sku]) || [];
  const skuPrefix = sku.match(/^([A-Za-z]+\d+)/)?.[1] || '';
  const priceCents = Number(productData.price);
  const compareAtPriceCents = Number(productData.compareAtPrice);
  const price = centsToCurrency(productData.price);
  const index = Number(productData.index);
  const item = {
    item_id: sku,
    item_name: cleanLabel(mapped[0]) || fallbackTitle,
    discount:
      Number.isFinite(compareAtPriceCents) &&
      Number.isFinite(priceCents) &&
      compareAtPriceCents > priceCents
        ? (compareAtPriceCents - priceCents) / 100
        : 0,
    index: Number.isFinite(index) && index > 0 ? index : 1,
    item_list_id: OE_AUDIO_LIST_ID,
    item_list_name: OE_AUDIO_ITEM_LIST_NAME,
    item_category: cleanLabel(mapped[2]) || skuPrefix,
    item_category2: cleanLabel(mapped[3]) || 'Standard',
    // Keep the Shopify option value exactly as configured (including German copy/casing).
    item_category3: cleanLabel(productData.color) || 'none',
    item_variant: cleanLabel(mapped[1]),
    item_brand: 'SHOKZ',
    price,
    quantity: 1,
    currency: OE_AUDIO_CURRENCY,
  };

  return {
    item_list_id: OE_AUDIO_LIST_ID,
    item_list_name: OE_AUDIO_LIST_ID,
    button_name: buttonName,
    items: [item],
  };
}

export function buildSubscriptionParameters(email, status = 'success', contentName = '') {
  const normalizedEmail = cleanLabel(email);
  const normalizedStatus = cleanLabel(status).toLowerCase();
  if (!['success', 'fail'].includes(normalizedStatus)) return null;
  if (normalizedStatus === 'success' && !normalizedEmail) return null;

  return {
    module: 'oe_audio_subscription',
    status: normalizedStatus,
    email: normalizedEmail,
    content_name: cleanLabel(contentName),
  };
}

export function getKlaviyoSubmitEmail(detail, expectedFormId) {
  if (detail?.type !== 'submit' || detail?.formId !== expectedFormId) return '';
  return cleanLabel(detail?.metaData?.$email);
}

function getKlaviyoFormEmail(container) {
  const emailInput = container?.querySelector?.('input[type="email"], input[name="email"]');
  return cleanLabel(emailInput?.value);
}

export function pushGa4Event(target, eventName, eventParameters) {
  const normalizedEventName = cleanLabel(eventName);
  if (!target || !normalizedEventName || !eventParameters || typeof eventParameters !== 'object') {
    return false;
  }

  try {
    target.dataLayer = target.dataLayer || [];
    target.dataLayer.push({ event_parameters: null });
    target.dataLayer.push({
      event: 'ga4Event',
      event_name: normalizedEventName,
      event_parameters: eventParameters,
    });
    return true;
  } catch {
    return false;
  }
}

export function buildLotteryRequest(email, activityId) {
  const formData = new FormData();
  formData.append('mb_id', email);
  formData.append('act_id', activityId);
  return formData;
}

export async function requestLottery(fetchImpl, endpoint, email, activityId) {
  const response = await fetchImpl(endpoint, {
    method: 'POST',
    body: buildLotteryRequest(email, activityId),
  });

  if (!response.ok) {
    throw new Error(`Lottery request failed with status ${response.status || 'unknown'}`);
  }

  return response.json();
}

export function resolveLotteryPrize(prizeId) {
  return LOTTERY_PRIZES[String(prizeId)] || null;
}

export function getLotteryRotation(prizeId) {
  return resolveLotteryPrize(prizeId)?.rotation ?? null;
}

export function getLotteryErrorKey(status) {
  return LOTTERY_ERROR_KEYS[status] || 'unknown';
}

export function findFirstPlayableIndex(scenes) {
  return scenes.findIndex((scene) => scene.hasVideo);
}

export function findNextPlayableIndex(scenes, currentIndex) {
  const playable = scenes
    .map((scene, index) => (scene.hasVideo ? index : -1))
    .filter((index) => index >= 0);

  if (playable.length === 0) return -1;

  const currentPosition = playable.indexOf(currentIndex);
  if (currentPosition < 0) return playable[0];

  return playable[(currentPosition + 1) % playable.length];
}

export function advancePhase(phase) {
  const index = EXPERIENCE_PHASES.indexOf(phase);
  if (index < 0) return EXPERIENCE_PHASES[0];

  return EXPERIENCE_PHASES[Math.min(index + 1, EXPERIENCE_PHASES.length - 1)];
}

export function shouldUnlockScroll(scenes, phase) {
  return findFirstPlayableIndex(scenes) < 0 || phase === 'playing';
}

export function createExperienceState(scenes) {
  return {
    phase: 'banner',
    activeIndex: findFirstPlayableIndex(scenes),
    wasPlaying: false,
  };
}

export function selectScene(state, scenes, index) {
  if (!scenes[index]) return state;

  return {
    phase: scenes[index].hasVideo ? state.phase : 'video-ready',
    activeIndex: index,
    wasPlaying: false,
  };
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class OpenEarAudioExperienceModule extends HTMLElementBase {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.timers = new Set();
    this.tabs = [...this.querySelectorAll('[data-scene-tab]')];
    this.panels = [...this.querySelectorAll('[data-scene-index][role="tabpanel"]')];
    this.sceneRegion = this.querySelector('[data-scene-region]');
    this.sceneCard = this.querySelector('[data-scene-card]');
    this.playButton = this.querySelector('[data-play]');
    this.pauseButton = this.querySelector('[data-pause]');
    this.volumeButton = this.querySelector('[data-volume]');
    this.fullscreenButton = this.querySelector('[data-fullscreen]');
    this.heroOverlay = this.querySelector('[data-hero-overlay]');
    this.heroMedia = this.querySelector('.oae-hero__media');
    this.scenes = this.panels.map((panel) => ({
      hasVideo: panel.dataset.hasVideo === 'true',
      panel,
      videos: [...panel.querySelectorAll('[data-scene-video]')],
      products: [...panel.querySelectorAll('[data-scene-product]')],
    }));
    this.state = createExperienceState(this.scenes);
    this.preferredMuted = false;

    if (this.state.activeIndex < 0 && this.scenes.length > 0) {
      this.state.activeIndex = 0;
    }

    this.bindEvents();
    this.observeScene();
    this.activateScene(this.state.activeIndex, { autoplay: false });
    this.render();

    if (findFirstPlayableIndex(this.scenes) < 0) {
      this.unlockScroll();
    } else {
      this.lockScroll();
      this.schedulePhaseTimer(1000);
    }
  }

  disconnectedCallback() {
    this.destroy();
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.eventRoot = this.closest('.oae-module') || this;
    this.eventRoot.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    window.addEventListener('wheel', (event) => this.handleProgressIntent(event), {
      passive: false,
      signal,
    });
    window.addEventListener('touchstart', (event) => this.handleProgressIntent(event), {
      passive: false,
      signal,
    });
    document.addEventListener(
      'fullscreenchange',
      () => {
        this.updateFullscreenLabel();
        this.render();
      },
      { signal },
    );

    this.viewportQuery = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
    this.viewportQuery?.addEventListener('change', () => this.handleViewportChange(), { signal });

    this.scenes.forEach((scene, index) => {
      scene.videos.forEach((video) => {
        video.addEventListener('ended', () => this.handleEnded(index), { signal });
      });
    });
  }

  getActiveVideo(scene) {
    if (!scene?.videos?.length) return null;
    if (scene.videos.length === 1) return scene.videos[0];

    const isMobile = Boolean(this.viewportQuery?.matches);
    const preferredClass = isMobile ? 'oae-scene__video--mo' : 'oae-scene__video--pc';
    return scene.videos.find((video) => video.classList.contains(preferredClass)) || scene.videos[0];
  }

  handleViewportChange() {
    const scene = this.scenes[this.state.activeIndex];
    const nextVideo = scene && this.getActiveVideo(scene);
    if (!nextVideo) return;

    const previousVideo =
      this.activeVideo && scene.videos.includes(this.activeVideo) && this.activeVideo !== nextVideo
        ? this.activeVideo
        : scene.videos.find((video) => video !== nextVideo && !video.paused);
    if (!previousVideo) return;

    const shouldResume = !previousVideo.paused && this.state.phase === 'playing';
    nextVideo.currentTime = previousVideo.currentTime;
    previousVideo.pause();
    this.activeVideo = nextVideo;
    if (!shouldResume) return;

    this.playActiveVideo({ preferMuted: this.preferredMuted });
  }

  observeScene() {
    if (!this.sceneCard || typeof IntersectionObserver === 'undefined') return;

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        const scene = this.scenes[this.state.activeIndex];
        const video = scene && this.getActiveVideo(scene);
        if (!video) return;

        if (entry.intersectionRatio < 0.5 && !video.paused) {
          this.pausedByVisibility = true;
          video.pause();
          return;
        }

        if (entry.intersectionRatio >= 0.5 && this.pausedByVisibility) {
          this.pausedByVisibility = false;
          this.playActiveVideo({ preferMuted: this.preferredMuted });
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    this.intersectionObserver.observe(this.sceneCard);
  }

  handleClick(event) {
    const moment = event.target.closest('.oae-moment[data-content-name]');
    if (moment) {
      this.trackSelectContent(moment, 'to_explore');
      return;
    }

    const tab = event.target.closest('[data-scene-tab]');
    if (tab) {
      this.trackSelectContent(tab, 'scene');
      this.activateScene(Number(tab.dataset.sceneIndex), { autoplay: true });
      return;
    }

    if (event.target.closest('[data-play]')) {
      this.playActiveVideo({ preferMuted: this.preferredMuted });
      return;
    }

    if (event.target.closest('[data-pause]')) {
      this.pauseActiveVideo();
      return;
    }

    if (event.target.closest('[data-volume]')) {
      this.toggleActiveVideoAudio();
      return;
    }

    if (event.target.closest('[data-fullscreen]')) {
      this.toggleFullscreen();
      return;
    }

    const product = event.target.closest('[data-scene-product]');
    if (product) {
      if (product.getAttribute('aria-hidden') !== 'true') this.trackProduct(product);
      return;
    }

    const advanceButton = event.target.closest('[data-experience-advance]');
    if (advanceButton) this.trackSelectContent(advanceButton, 'oe_audio');

    if ((advanceButton || event.target.closest('[data-hero]')) && this.state.phase !== 'playing') {
      event.preventDefault();
      this.advanceExperience();
    }
  }

  trackSelectContent(element, fallbackContentType) {
    const parameters = buildSelectContentParameters(
      element.dataset.contentType || fallbackContentType,
      element.dataset.contentName,
      element.dataset.buttonName || element.textContent,
    );
    if (parameters) pushGa4Event(window, 'select_content', parameters);
  }

  trackProduct(element) {
    const parameters = buildSelectItemParameters(
      {
        sku: element.dataset.productSku,
        variantId: element.dataset.productVariantId,
        title: element.dataset.productTitle,
        price: element.dataset.productPrice,
        compareAtPrice: element.dataset.productCompareAtPrice,
        color: element.dataset.productColor,
        index: element.dataset.productIndex,
        buttonName: element.dataset.buttonName,
      },
      getProductValueMap(),
    );
    if (parameters) pushGa4Event(window, 'select_item', parameters);
  }

  handleKeydown(event) {
    const tab = event.target.closest('[data-scene-tab]');
    if (!tab) return;

    const tablist = tab.closest('[role="tablist"]');
    const groupTabs = [...tablist.querySelectorAll('[data-scene-tab]')];
    const currentIndex = groupTabs.indexOf(tab);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % groupTabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + groupTabs.length) % groupTabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = groupTabs.length - 1;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.trackSelectContent(tab, 'scene');
      this.activateScene(Number(tab.dataset.sceneIndex), { autoplay: true });
      return;
    } else {
      return;
    }

    event.preventDefault();
    groupTabs[nextIndex].focus();
  }

  handleProgressIntent(event) {
    if (this.state.phase === 'playing' || !this.isScrollLocked) return;

    event.preventDefault();
    const now = Date.now();
    if (now - (this.lastProgressIntentAt || 0) < 450) return;

    this.lastProgressIntentAt = now;
    this.advanceExperience();
  }

  advanceExperience() {
    if (this.state.phase === 'playing') return;

    // Keep the full-screen-to-card transition intact when extra wheel/touch
    // events arrive while the page is still scroll-locked.
    if (this.state.phase === 'shrink' && this.shrinkTransitionActive) return;

    this.clearPhaseTimer();
    if (this.state.phase === 'video-ready') {
      this.playActiveVideo();
      return;
    }

    this.state.phase = advancePhase(this.state.phase);
    if (this.state.phase === 'shrink') {
      this.focusScene();
      this.render();
      this.startShrinkTransition();
      return;
    }

    if (this.state.phase === 'video-ready') {
      this.schedulePhaseTimer(1000);
    } else {
      this.schedulePhaseTimer(3000);
    }
    this.render();
  }

  startShrinkTransition() {
    if (!this.sceneCard || !this.heroMedia) {
      this.completeShrinkTransition();
      return;
    }

    const targetRect = this.sceneCard.getBoundingClientRect();
    const duration = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 900;
    const shrinkCard = document.createElement('div');

    this.shrinkTransitionActive = true;
    this.shrinkCard = shrinkCard;
    shrinkCard.className = 'oae-hero-shrink-card is-shrink-start';
    shrinkCard.setAttribute('aria-hidden', 'true');
    shrinkCard.style.setProperty('--oae-shrink-target-top', `${targetRect.top}px`);
    shrinkCard.style.setProperty('--oae-shrink-target-left', `${targetRect.left}px`);
    shrinkCard.style.setProperty('--oae-shrink-target-width', `${targetRect.width}px`);
    shrinkCard.style.setProperty('--oae-shrink-target-height', `${targetRect.height}px`);
    shrinkCard.append(this.heroMedia.cloneNode(true));
    this.append(shrinkCard);

    // Commit the full-screen frame before transitioning to the measured card.
    shrinkCard.getBoundingClientRect();
    this.shrinkFrame = window.requestAnimationFrame(() => {
      shrinkCard.classList.remove('is-shrink-start');
    });

    this.shrinkTimer = this.setTimer(() => this.completeShrinkTransition(), duration + 100);
  }

  completeShrinkTransition() {
    if (this.shrinkFrame) window.cancelAnimationFrame(this.shrinkFrame);
    this.shrinkFrame = null;
    this.clearTimer(this.shrinkTimer);
    this.shrinkTimer = null;
    this.shrinkTransitionActive = false;

    this.shrinkCard?.remove();
    this.shrinkCard = null;

    if (this.state.phase !== 'shrink') return;

    this.state.phase = advancePhase(this.state.phase);
    this.render();
    this.schedulePhaseTimer(1000);
  }

  schedulePhaseTimer(delay) {
    this.clearPhaseTimer();
    this.phaseTimer = this.setTimer(() => {
      this.phaseTimer = null;
      this.advanceExperience();
    }, delay);
  }

  setTimer(callback, delay) {
    const timer = window.setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, delay);
    this.timers.add(timer);
    return timer;
  }

  clearTimer(timer) {
    if (!timer) return;
    window.clearTimeout(timer);
    this.timers.delete(timer);
  }

  clearPhaseTimer() {
    this.clearTimer(this.phaseTimer);
    this.phaseTimer = null;
  }

  async playActiveVideo({ preferMuted = this.preferredMuted } = {}) {
    const activeIndex = this.state.activeIndex;
    const scene = this.scenes[activeIndex];
    const video = scene && this.getActiveVideo(scene);
    if (!video) {
      this.state.phase = 'video-ready';
      this.state.wasPlaying = false;
      this.render();
      return false;
    }

    video.playsInline = true;
    const shouldStartMuted = Boolean(preferMuted);
    video.muted = shouldStartMuted;
    try {
      await video.play();
    } catch {
      if (activeIndex !== this.state.activeIndex) {
        video.pause();
        return false;
      }

      if (shouldStartMuted) {
        this.state.phase = 'video-ready';
        this.state.wasPlaying = false;
        this.render();
        return false;
      }

      video.muted = true;
      try {
        await video.play();
        this.preferredMuted = true;
      } catch {
        if (activeIndex !== this.state.activeIndex) return false;
        video.muted = this.preferredMuted;
        this.state.phase = 'video-ready';
        this.state.wasPlaying = false;
        this.render();
        return false;
      }
    }

    if (activeIndex !== this.state.activeIndex) {
      video.pause();
      return false;
    }

    this.state.phase = 'playing';
    this.state.wasPlaying = true;
    this.activeVideo = video;
    this.preferredMuted = video.muted;
    this.dataset.productVisible = 'false';
    this.unlockScroll();
    this.render();
    this.clearTimer(this.productTimer);
    this.productTimer = this.setTimer(() => {
      this.dataset.productVisible = 'true';
      this.renderProducts();
    }, 2000);
    return true;
  }

  async toggleActiveVideoAudio() {
    const scene = this.scenes[this.state.activeIndex];
    const video =
      scene && this.activeVideo && scene.videos.includes(this.activeVideo)
        ? this.activeVideo
        : this.getActiveVideo(scene);
    if (!video) return false;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    this.preferredMuted = nextMuted;
    if (nextMuted) {
      this.render();
      return true;
    }

    try {
      if (video.paused) await video.play();
      this.activeVideo = video;
      this.render();
      return true;
    } catch {
      video.muted = true;
      this.preferredMuted = true;
      try {
        if (video.paused) await video.play();
        this.render();
      } catch {
        this.state.phase = 'video-ready';
        this.state.wasPlaying = true;
        this.render();
      }
      return false;
    }
  }

  pauseActiveVideo() {
    const scene = this.scenes[this.state.activeIndex];
    const video =
      scene && this.activeVideo && scene.videos.includes(this.activeVideo)
        ? this.activeVideo
        : this.getActiveVideo(scene);
    if (!video || video.paused) return false;

    video.pause();
    this.activeVideo = video;
    this.pausedByVisibility = false;
    this.clearPhaseTimer();
    this.clearTimer(this.productTimer);
    this.productTimer = null;
    this.dataset.productVisible = 'false';
    this.state.phase = 'video-ready';
    this.state.wasPlaying = true;
    this.render();
    return true;
  }

  activateScene(index, { autoplay = true, preferMuted = this.preferredMuted } = {}) {
    if (!this.scenes[index]) return;

    this.clearTimer(this.productTimer);
    this.productTimer = null;
    this.dataset.productVisible = 'false';
    this.pausedByVisibility = false;
    this.activeVideo = null;

    this.scenes.forEach((scene, sceneIndex) => {
      scene.videos.forEach((video) => {
        video.pause();
        if (sceneIndex !== index) video.currentTime = 0;
      });
    });

    this.state = selectScene(this.state, this.scenes, index);
    if (autoplay) this.state.phase = 'video-ready';
    this.render();

    if (autoplay && this.scenes[index].hasVideo) {
      this.playActiveVideo({ preferMuted });
    }
  }

  handleEnded(index) {
    if (index !== this.state.activeIndex) return;

    const nextIndex = findNextPlayableIndex(this.scenes, index);
    if (nextIndex < 0) return;

    if (nextIndex === index) {
      const video = this.getActiveVideo(this.scenes[index]);
      if (video) video.currentTime = 0;
      this.playActiveVideo({ preferMuted: this.preferredMuted });
      return;
    }

    this.activateScene(nextIndex, { autoplay: true, preferMuted: this.preferredMuted });
  }

  render() {
    this.dataset.phase = this.state.phase;
    this.dataset.activeScene = String(this.state.activeIndex);
    if (this.heroOverlay) {
      this.heroOverlay.setAttribute('aria-hidden', String(this.state.phase !== 'overlay'));
    }

    this.tabs.forEach((tab) => {
      const isActive = Number(tab.dataset.sceneIndex) === this.state.activeIndex;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.scenes.forEach((scene, index) => {
      const isActive = index === this.state.activeIndex;
      scene.panel.hidden = !isActive;
      scene.panel.classList.toggle('is-active', isActive);
    });

    const activeScene = this.scenes[this.state.activeIndex];
    const canPlay = Boolean(activeScene?.hasVideo);
    const isPlaying = this.state.phase === 'playing';
    const isPaused = this.state.phase === 'video-ready' && this.state.wasPlaying;
    if (this.playButton) {
      this.playButton.hidden = this.state.phase !== 'video-ready';
      this.playButton.disabled = !canPlay;
      this.playButton.setAttribute('aria-disabled', String(!canPlay));
    }
    if (this.pauseButton) {
      this.pauseButton.hidden = !canPlay || !isPlaying;
    }
    if (this.volumeButton) {
      const activeVideo =
        activeScene && this.activeVideo && activeScene.videos.includes(this.activeVideo)
          ? this.activeVideo
          : this.getActiveVideo(activeScene);
      const isMuted = activeVideo?.muted ?? this.preferredMuted;
      this.volumeButton.hidden = !canPlay || !isPlaying;
      this.volumeButton.dataset.muted = String(isMuted);
      this.volumeButton.setAttribute(
        'aria-label',
        isMuted ? this.volumeButton.dataset.unmuteLabel : this.volumeButton.dataset.muteLabel,
      );
    }
    if (this.fullscreenButton) {
      this.fullscreenButton.hidden =
        !canPlay ||
        !this.sceneCard?.requestFullscreen ||
        (!isPlaying && !(isPaused && document.fullscreenElement === this.sceneCard));
    }

    this.renderProducts();
  }

  renderProducts() {
    const productVisible = this.dataset.productVisible === 'true';
    this.scenes.forEach((scene, index) => {
      scene.products.forEach((product) => {
        const isVisible = productVisible && index === this.state.activeIndex;
        product.setAttribute('aria-hidden', String(!isVisible));
        product.tabIndex = isVisible ? 0 : -1;
      });
    });
  }

  focusScene() {
    if (!this.sceneRegion) return;

    const targetY = this.sceneRegion.getBoundingClientRect().top + window.scrollY;
    if (this.isScrollLocked) {
      this.lockedScrollY = targetY;
      document.body.style.top = `-${targetY}px`;
    } else {
      window.scrollTo({ top: targetY, behavior: 'auto' });
    }
  }

  prepareForAnchorNavigation() {
    this.clearPhaseTimer();
    this.clearTimer(this.shrinkTimer);
    this.shrinkTimer = null;
    this.shrinkTransitionActive = false;

    if (this.shrinkFrame) window.cancelAnimationFrame(this.shrinkFrame);
    this.shrinkFrame = null;
    this.shrinkCard?.remove();
    this.shrinkCard = null;

    this.clearTimer(this.productTimer);
    this.productTimer = null;
    this.scenes.forEach((scene) => scene.videos.forEach((video) => video.pause()));
    this.state.phase = 'video-ready';
    this.state.wasPlaying = false;
    this.unlockScroll();
    this.render();
  }

  lockScroll() {
    if (this.isScrollLocked || window.Shopify?.designMode) return;

    this.isScrollLocked = true;
    this.lockedScrollY = window.scrollY;
    this.previousBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${this.lockedScrollY}px`,
      left: '0',
      right: '0',
      width: '100%',
      overflow: 'hidden',
    });
  }

  unlockScroll() {
    if (!this.isScrollLocked) return;

    const targetY = this.lockedScrollY || 0;
    Object.assign(document.body.style, this.previousBodyStyle);
    this.isScrollLocked = false;
    window.scrollTo({ top: targetY, behavior: 'auto' });
  }

  async toggleFullscreen() {
    if (!this.sceneCard?.requestFullscreen) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await this.sceneCard.requestFullscreen();
      }
    } catch {
      // Fullscreen can be rejected when the browser does not treat the event as a user gesture.
    }
  }

  updateFullscreenLabel() {
    if (!this.fullscreenButton) return;
    this.fullscreenButton.setAttribute(
      'aria-label',
      document.fullscreenElement ? this.dataset.exitFullscreenLabel : this.dataset.enterFullscreenLabel,
    );
  }

  destroy() {
    if (!this.initialized) return;

    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    if (this.shrinkFrame) window.cancelAnimationFrame(this.shrinkFrame);
    this.shrinkCard?.remove();
    this.timers?.forEach((timer) => window.clearTimeout(timer));
    this.timers?.clear();
    this.scenes?.forEach((scene) => scene.videos?.forEach((video) => video.pause()));
    this.unlockScroll();
    this.initialized = false;
  }
}

export class OpenEarLotteryModule extends HTMLElementBase {
  connectedCallback() {
    if (this.initialized) return;

    this.klaviyoFormId = cleanLabel(this.dataset.klaviyoFormId);
    this.klaviyoContainer = this.klaviyoFormId
      ? this.querySelector(`.klaviyo-form-${this.klaviyoFormId}`)
      : null;
    this.rotor = this.querySelector('[data-lottery-rotor]');
    this.errorMessage = this.querySelector('[data-lottery-error]');
    this.overlay = this.querySelector('[data-lottery-overlay]');
    this.modal = this.querySelector('[data-lottery-modal]');
    this.panels = [...this.querySelectorAll('[data-lottery-panel]')];
    this.abortController = new AbortController();
    this.subscriptionAttempt = null;
    this.subscriptionFailureDelayMs = SUBSCRIPTION_FAILURE_DELAY_MS;
    this.initialized = true;

    const { signal } = this.abortController;

    window.addEventListener('klaviyoForms', (event) => this.handleKlaviyoEvent(event), { signal });
    this.klaviyoContainer?.addEventListener(
      'click',
      (event) => this.handleKlaviyoSubmitClick(event),
      { capture: true, signal },
    );
    this.querySelectorAll('[data-lottery-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeModal(), { signal });
    });
    this.overlay?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.overlay) this.closeModal();
      },
      { signal },
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.overlay && !this.overlay.hidden) this.closeModal();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.resultTimer) window.clearTimeout(this.resultTimer);
    this.resetSubscriptionAttempt();
    this.unlockScroll();
    this.initialized = false;
  }

  handleKlaviyoSubmitClick(event) {
    const submitControl = event.target?.closest?.('button, input[type="submit"]');
    if (
      !submitControl ||
      !this.klaviyoContainer?.contains(submitControl) ||
      this.isSubmitting ||
      this.completed
    ) {
      return;
    }

    this.startSubscriptionAttempt(getKlaviyoFormEmail(this.klaviyoContainer));
  }

  startSubscriptionAttempt(email) {
    this.resetSubscriptionAttempt();

    const attempt = {
      email: cleanLabel(email),
      status: 'pending',
      submitHandled: false,
      timer: null,
    };
    const configuredDelay = Number(this.subscriptionFailureDelayMs);
    const failureDelay = Number.isFinite(configuredDelay)
      ? Math.max(0, configuredDelay)
      : SUBSCRIPTION_FAILURE_DELAY_MS;

    this.subscriptionAttempt = attempt;
    attempt.timer = window.setTimeout(() => {
      if (this.subscriptionAttempt !== attempt || attempt.status !== 'pending') return;
      this.settleSubscriptionAttempt('fail');
    }, failureDelay);
  }

  clearSubscriptionAttemptTimer() {
    if (this.subscriptionAttempt?.timer != null) {
      window.clearTimeout(this.subscriptionAttempt.timer);
      this.subscriptionAttempt.timer = null;
    }
  }

  resetSubscriptionAttempt() {
    this.clearSubscriptionAttemptTimer();
    this.subscriptionAttempt = null;
  }

  settleSubscriptionAttempt(status, email = '') {
    const attempt = this.subscriptionAttempt;
    if (!attempt || attempt.status !== 'pending') return false;

    const trackedEmail = cleanLabel(email) || attempt.email;
    const subscriptionParameters = buildSubscriptionParameters(trackedEmail, status, '');
    if (!subscriptionParameters) return false;

    this.clearSubscriptionAttemptTimer();
    attempt.email = trackedEmail;
    attempt.status = subscriptionParameters.status;

    return pushGa4Event(window, 'subscribe', subscriptionParameters);
  }

  async handleKlaviyoEvent(event) {
    const email = getKlaviyoSubmitEmail(event?.detail, this.klaviyoFormId);
    if (!email || this.isSubmitting || this.completed) return;

    const currentAttempt = this.subscriptionAttempt;
    if (currentAttempt?.submitHandled) return;

    if (
      !currentAttempt ||
      (currentAttempt.status === 'fail' && currentAttempt.email !== email)
    ) {
      this.startSubscriptionAttempt(email);
    }

    this.subscriptionAttempt.submitHandled = true;
    this.settleSubscriptionAttempt('success', email);
    await this.runLottery(email);
  }

  async runLottery(email) {
    const endpoint = this.dataset.lotteryEndpoint?.trim();
    const activityId = this.dataset.lotteryActivityId?.trim();
    if (!endpoint || !activityId || !email) {
      this.showError('unknown');
      return;
    }

    this.isSubmitting = true;
    this.setBusy(true);
    this.clearError();

    try {
      const payload = await requestLottery(window.fetch.bind(window), endpoint, email, activityId);
      if (payload?.status !== 'ok') {
        this.showError(getLotteryErrorKey(payload?.data?.act_status));
        return;
      }

      const prize = resolveLotteryPrize(payload?.data?.act_price_id);
      if (!prize) {
        this.showError('unknown');
        return;
      }

      this.dataset.lotteryState = 'spinning';
      this.rotor?.style.setProperty('--oae-lottery-rotation', `${prize.rotation}deg`);
      await this.waitForSpin();
      this.showResult(prize);
    } catch {
      this.showError('unknown');
    } finally {
      this.isSubmitting = false;
      if (!this.completed) this.setBusy(false);
    }
  }

  waitForSpin() {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 50 : 3000;

    return new Promise((resolve) => {
      this.resultTimer = window.setTimeout(() => {
        this.resultTimer = null;
        resolve();
      }, duration);
    });
  }

  setBusy(isBusy) {
    this.setAttribute('aria-busy', String(isBusy));
    this.klaviyoContainer?.setAttribute('aria-busy', String(isBusy));
  }

  clearError() {
    if (!this.errorMessage) return;

    this.errorMessage.hidden = true;
    this.errorMessage.textContent = '';
  }

  showError(errorKey) {
    if (!this.errorMessage) return;

    const copyKey = LOTTERY_ERROR_COPY_KEYS[errorKey] || LOTTERY_ERROR_COPY_KEYS.unknown;
    this.errorMessage.textContent = this.dataset[copyKey] || this.dataset.errorUnknown || '';
    this.errorMessage.hidden = false;
  }

  showResult(prize) {
    const panel = this.panels.find((candidate) => candidate.dataset.lotteryPanel === prize.key);
    if (!panel || !this.overlay) {
      this.showError('unknown');
      this.setBusy(false);
      return;
    }

    this.completed = true;
    this.dataset.lotteryState = 'complete';
    this.setBusy(false);
    if (this.klaviyoContainer) this.klaviyoContainer.hidden = true;
    this.openModal(panel);
  }

  openModal(panel) {
    this.panels.forEach((candidate) => {
      candidate.hidden = candidate !== panel;
    });
    this.overlay.hidden = false;
    this.lockScroll();
    panel.querySelector('[data-lottery-close]')?.focus({ preventScroll: true });
  }

  closeModal() {
    if (!this.overlay || this.overlay.hidden) return;

    this.overlay.hidden = true;
    this.unlockScroll();
  }

  lockScroll() {
    if (this.isScrollLocked || window.Shopify?.designMode) return;

    this.isScrollLocked = true;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this.isScrollLocked) return;

    document.body.style.overflow = this.previousBodyOverflow || '';
    this.isScrollLocked = false;
  }
}

export class OpenEarMomentsModule extends HTMLElementBase {
  connectedCallback() {
    if (this.initialized) return;

    this.abortController = new AbortController();
    this.initialized = true;
    this.addEventListener(
      'click',
      (event) => {
        const moment = event.target.closest('.oae-moment[data-content-name]');
        if (!moment) return;

        const parameters = buildSelectContentParameters(
          moment.dataset.contentType || 'to_explore',
          moment.dataset.contentName,
          moment.dataset.buttonName || moment.textContent,
        );
        if (parameters) pushGa4Event(window, 'select_content', parameters);
      },
      { signal: this.abortController.signal },
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.initialized = false;
  }
}

if (
  typeof window !== 'undefined' &&
  typeof customElements !== 'undefined' &&
  !customElements.get('open-ear-audio-experience-module')
) {
  customElements.define('open-ear-audio-experience-module', OpenEarAudioExperienceModule);
}

if (
  typeof window !== 'undefined' &&
  typeof customElements !== 'undefined' &&
  !customElements.get('open-ear-lottery-module')
) {
  customElements.define('open-ear-lottery-module', OpenEarLotteryModule);
}

if (
  typeof window !== 'undefined' &&
  typeof customElements !== 'undefined' &&
  !customElements.get('open-ear-moments-module')
) {
  customElements.define('open-ear-moments-module', OpenEarMomentsModule);
}
